/**
 * Day 2: Visual Calendar V2 — Shift Status Display Adapter
 * Single source of truth for status-to-display mapping.
 * 
 * Rules:
 * - Display only — does NOT infer or transform statuses
 * - Supports all backend canonical statuses
 * - CONFIRMED marked as isLegacy (never produced by backend, kept for backward compat)
 * - Hard fallback for unknown statuses (never returns undefined, never blank badge)
 */

import { AlertTriangle, CheckCircle, Clock, XCircle, Eye, Ban, FileText, HelpCircle, UserX } from 'lucide-react';

const STATUS_REGISTRY = {
  OPEN: {
    label: 'Open',
    colorClass: 'text-red-800',
    bgClass: 'bg-red-100 border-red-300 text-red-800',
    dotColor: 'bg-red-500',
    icon: AlertTriangle,
    isLegacy: false,
  },
  PARTIALLY_FILLED: {
    label: 'Partial',
    colorClass: 'text-amber-800',
    bgClass: 'bg-amber-100 border-amber-300 text-amber-800',
    dotColor: 'bg-amber-500',
    icon: Clock,
    isLegacy: false,
  },
  ASSIGNED: {
    label: 'Assigned',
    colorClass: 'text-green-800',
    bgClass: 'bg-green-100 border-green-300 text-green-800',
    dotColor: 'bg-green-500',
    icon: CheckCircle,
    isLegacy: false,
  },
  CONFIRMED: {
    label: 'Confirmed',
    colorClass: 'text-blue-800',
    bgClass: 'bg-blue-100 border-blue-300 text-blue-800',
    dotColor: 'bg-blue-500',
    icon: CheckCircle,
    isLegacy: true, // Not produced by calculate_shift_status — kept for backward compat
  },
  IN_PROGRESS: {
    label: 'In Progress',
    colorClass: 'text-purple-800',
    bgClass: 'bg-purple-100 border-purple-300 text-purple-800',
    dotColor: 'bg-purple-500',
    icon: Clock,
    isLegacy: false,
  },
  COMPLETED: {
    label: 'Completed',
    colorClass: 'text-gray-600',
    bgClass: 'bg-gray-100 border-gray-300 text-gray-600',
    dotColor: 'bg-gray-500',
    icon: CheckCircle,
    isLegacy: false,
  },
  CANCELLED: {
    label: 'Cancelled',
    colorClass: 'text-gray-400',
    bgClass: 'bg-gray-50 border-gray-200 text-gray-400 line-through',
    dotColor: 'bg-gray-400',
    icon: XCircle,
    isLegacy: false,
  },
  NO_SHOW: {
    label: 'No Show',
    colorClass: 'text-rose-800',
    bgClass: 'bg-rose-100 border-rose-300 text-rose-800',
    dotColor: 'bg-rose-500',
    icon: UserX,
    isLegacy: false,
  },
  INVOICED: {
    label: 'Invoiced',
    colorClass: 'text-sky-800',
    bgClass: 'bg-sky-100 border-sky-300 text-sky-800',
    dotColor: 'bg-sky-500',
    icon: FileText,
    isLegacy: false,
  },
};

const FALLBACK_STATUS = {
  label: 'Unknown',
  colorClass: 'text-gray-500',
  bgClass: 'bg-gray-100 border-gray-200 text-gray-500',
  dotColor: 'bg-gray-400',
  icon: HelpCircle,
  isLegacy: false,
};

/**
 * Get display metadata for a shift status.
 * Never returns undefined. Always returns a valid display object.
 * @param {string} status - Backend canonical status string
 * @returns {{ label: string, colorClass: string, bgClass: string, dotColor: string, icon: React.ComponentType, isLegacy: boolean }}
 */
export function getShiftStatusMeta(status) {
  if (!status) return FALLBACK_STATUS;
  return STATUS_REGISTRY[status] || FALLBACK_STATUS;
}

/**
 * Get all known statuses for filter dropdowns.
 * Excludes legacy statuses from active filter options.
 */
export function getActiveStatuses() {
  return Object.entries(STATUS_REGISTRY)
    .filter(([, meta]) => !meta.isLegacy)
    .map(([key, meta]) => ({ value: key, label: meta.label }));
}

/**
 * Get all statuses including legacy for display purposes.
 */
export function getAllStatuses() {
  return Object.entries(STATUS_REGISTRY)
    .map(([key, meta]) => ({ value: key, label: meta.label, isLegacy: meta.isLegacy }));
}
